import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Search, Phone, Mail, Factory, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// US state full names for SEO and display
const STATE_NAMES = {
  AL:"Alabama", AK:"Alaska", AZ:"Arizona", AR:"Arkansas", CA:"California", CO:"Colorado",
  CT:"Connecticut", DE:"Delaware", FL:"Florida", GA:"Georgia", HI:"Hawaii", ID:"Idaho",
  IL:"Illinois", IN:"Indiana", IA:"Iowa", KS:"Kansas", KY:"Kentucky", LA:"Louisiana",
  ME:"Maine", MD:"Maryland", MA:"Massachusetts", MI:"Michigan", MN:"Minnesota", MS:"Mississippi",
  MO:"Missouri", MT:"Montana", NE:"Nebraska", NV:"Nevada", NH:"New Hampshire", NJ:"New Jersey",
  NM:"New Mexico", NY:"New York", NC:"North Carolina", ND:"North Dakota", OH:"Ohio", OK:"Oklahoma",
  OR:"Oregon", PA:"Pennsylvania", RI:"Rhode Island", SC:"South Carolina", SD:"South Dakota",
  TN:"Tennessee", TX:"Texas", UT:"Utah", VT:"Vermont", VA:"Virginia", WA:"Washington",
  WV:"West Virginia", WI:"Wisconsin", WY:"Wyoming", DC:"District of Columbia",
};

export default function Dealers() {
  const [dealers, setDealers] = useState([]);
  const [states, setStates] = useState([]);
  const [state, setState] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Find a Lumber Dealer — LumberLoop USA";
  }, []);

  const fetchDealers = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (state !== "all") params.state = state;
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/public/dealers`, { params }),
        axios.get(`${API}/public/dealer-states`),
      ]);
      setDealers(d.data);
      setStates(s.data);
    } catch (e) {
      console.error("Failed to load dealers:", e);
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => { fetchDealers(); }, [fetchDealers]);

  const filtered = useMemo(() => {
    if (!query) return dealers;
    const q = query.toLowerCase();
    return dealers.filter((d) =>
      (d.name || "").toLowerCase().includes(q) ||
      (d.city || "").toLowerCase().includes(q) ||
      (d.zip_code || "").includes(query) ||
      (d.manufacturer_name || "").toLowerCase().includes(q)
    );
  }, [dealers, query]);

  const grouped = useMemo(() => {
    const g = {};
    for (const d of filtered) {
      const key = d.state || "—";
      (g[key] = g[key] || []).push(d);
    }
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div data-testid="dealer-locator-page">
      {/* HERO */}
      <section className="relative border-b-2 border-[#1A365D] bg-[#1A365D] text-white overflow-hidden">
        <div className="stripes-stars absolute inset-0 opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">Public Dealer Locator</div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase mt-2 leading-none">
            Find a lumber dealer<br/><span className="text-[#C62828]">near you.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl">
            Browse every dealer registered on LumberLoop USA — by state, city, ZIP, or manufacturer. Reach out directly, or submit a quote request and we&apos;ll route it for you.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/request-quote" className="btn-primary" data-testid="locator-cta-quote">Contractor Quote Request</Link>
            <Link to="/register" className="btn-secondary !bg-white" data-testid="locator-cta-mfr">Manufacturer Portal</Link>
          </div>
        </div>
        <div className="h-2 stripes-flag relative"></div>
      </section>

      {/* FILTERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="label-tag block mb-2">Filter by State</label>
            <select value={state} onChange={(e) => setState(e.target.value)} className="ll-select" data-testid="locator-state-select">
              <option value="all">All States</option>
              {states.map((s) => <option key={s} value={s}>{s} — {STATE_NAMES[s] || s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-tag block mb-2">Search by City, ZIP, or Dealer Name</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Austin or 78704" className="ll-input pl-9" data-testid="locator-search-input"/>
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="font-mono-ll text-xs tracking-widest text-slate-500 uppercase" data-testid="locator-result-count">
            {loading ? "Loading…" : `${filtered.length} dealer${filtered.length === 1 ? "" : "s"} found${state !== "all" ? ` in ${STATE_NAMES[state] || state}` : ""}`}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        {loading ? (
          <div className="py-20 text-center font-mono-ll text-sm text-slate-500 tracking-widest">LOADING…</div>
        ) : grouped.length === 0 ? (
          <div className="brutal-card-flat p-12 text-center">
            <MapPin className="mx-auto text-slate-400" size={48} strokeWidth={1.5}/>
            <h2 className="mt-4 font-heading text-2xl font-bold uppercase text-[#1A365D]">No dealers found</h2>
            <p className="mt-2 text-slate-600">Try a different state, or remove your search filter.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {grouped.map(([st, items]) => (
              <div key={st} data-testid={`state-group-${st}`}>
                <div className="flex items-baseline gap-4 mb-5 border-b-2 border-[#1A365D] pb-3">
                  <h2 className="font-heading text-3xl md:text-4xl font-black uppercase text-[#1A365D] leading-none">
                    {STATE_NAMES[st] || st}
                  </h2>
                  <span className="font-mono-ll text-xs tracking-widest text-[#C62828]">{items.length} DEALER{items.length === 1 ? "" : "S"}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((d) => (
                    <div key={d.id || `${d.name}-${d.zip_code}`} className="brutal-card-flat p-5 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform" data-testid={`dealer-card`}>
                      <div className="flex items-start gap-2">
                        <MapPin className="text-[#C62828] mt-1 flex-shrink-0" size={18} strokeWidth={2}/>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-lg font-bold uppercase text-[#1A365D] leading-tight truncate">{d.name}</h3>
                          <div className="text-sm text-slate-700">{d.city}, {d.state} {d.zip_code}</div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1.5 text-sm">
                        {d.phone && (
                          <a href={`tel:${d.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-[#C62828]">
                            <Phone size={13}/> {d.phone}
                          </a>
                        )}
                        {d.email && (
                          <a href={`mailto:${d.email}`} className="flex items-center gap-2 text-slate-700 hover:text-[#C62828] truncate">
                            <Mail size={13}/> <span className="truncate">{d.email}</span>
                          </a>
                        )}
                      </div>
                      {d.manufacturer_name && (
                        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2">
                          <Factory size={13} className="text-slate-500"/>
                          <span className="font-mono-ll text-[10px] tracking-widest text-slate-500 uppercase truncate">{d.manufacturer_name}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* MANUFACTURER CTA */}
      <section className="bg-[#EAEAE5] border-t-2 border-[#1A365D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="label-tag">For Manufacturers</div>
            <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 leading-none text-[#1A365D]">
              Get your dealers <span className="text-[#C62828]">on the map.</span>
            </h2>
            <p className="mt-4 text-slate-700 max-w-2xl">
              Every dealer you list shows up here for free — instant visibility to contractors searching for lumber suppliers in their area, plus automatic lead routing when they submit a quote request that matches your network.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="locator-bottom-cta">
              Join the Network <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
